/**
 * Progress per exercise, derived from the finished sessions (nothing here is stored, so editing or deleting a session
 * changes every number at once): the metrics the charts plot, the comparison of each time with the last time and the
 * best before, the records (Strong's "Rekorde") and which records a session set.
 *
 * Only done sets that are not warm-ups count. An exercise done twice in one session counts as one entry with all its
 * sets. The two variants of an exercise (reps and timed, todo 16) are kept apart: they are not comparable.
 */
import { asEntry, entryMeasure, setFields, type Exercise, type Measure } from "./exercises";
import { sameVariant, type EntryMatch, type Session, type SessionSet } from "./sessions";

type Measured = Pick<Exercise, "equipment" | "measure">;

/** One time the exercise was done: the session and the exercise's sets in it (warm-ups included, in order). */
export interface Entry {
  sessionId: string;
  name: string;
  endedAt: number;
  sets: SessionSet[];
}

/** Chronological: by end time, then id (so the order is stable when two sessions end in the same millisecond). */
export function sortSessions(sessions: Session[]): Session[] {
  return [...sessions].sort((a, b) => a.endedAt - b.endedAt || a.id.localeCompare(b.id));
}

/** Every time the exercise was done (an id, or `sameVariant` for one variant), oldest first. */
export function exerciseEntries(sessions: Session[], which: EntryMatch): Entry[] {
  const match = typeof which === "string" ? (x: { exerciseId: string }) => x.exerciseId === which : which;
  const out: Entry[] = [];
  for (const s of sortSessions(sessions)) {
    const sets = s.exercises.filter(match).flatMap((x) => x.sets);
    if (sets.length) out.push({ sessionId: s.id, name: s.name, endedAt: s.endedAt, sets });
  }
  return out;
}

/** The sets that count for records and trends: done and not a warm-up. */
export const workingSets = (sets: SessionSet[]) => sets.filter((s) => s.done && s.type !== "warmup");

const round1 = (n: number) => Math.round(n * 10) / 10;
const pos = (n: number | undefined): n is number => n !== undefined && Number.isFinite(n) && n > 0;

/** Estimated one rep max after Epley: weight × (1 + reps / 30). Needs a weight and at least one rep. */
export function e1rm(s: SessionSet): number | undefined {
  return pos(s.weightKg) && pos(s.reps) ? round1(s.weightKg * (1 + s.reps / 30)) : undefined;
}

/** Seconds per 100 m (lower is faster). */
export function pace(s: SessionSet): number | undefined {
  return pos(s.distanceM) && pos(s.durationSec) ? round1((s.durationSec / s.distanceM) * 100) : undefined;
}

const weighted = (e: Measured) => e.measure === "reps" && setFields(e).weight;

// --- metrics (what the charts plot) ---

/**
 * weighted reps: e1rm, maxWeight, volume (Σ weight × reps); bodyweight reps: maxReps, totalReps; timed: reps in a set
 * of one duration and their sum (only sets of the same duration are compared); hold: longest; distance: longest set,
 * sum, and pace.
 */
export type MetricKey =
  | "e1rm" | "maxWeight" | "volume" | "maxReps" | "totalReps" | "timedReps" | "timedTotal" | "longestHold"
  | "distance" | "totalDistance" | "pace";

export type Unit = "kg" | "reps" | "sec" | "m" | "pace";

export const METRICS: Record<MetricKey, { unit: Unit; lowerIsBetter?: boolean; byDuration?: boolean }> = {
  e1rm: { unit: "kg" },
  maxWeight: { unit: "kg" },
  volume: { unit: "kg" },
  maxReps: { unit: "reps" },
  totalReps: { unit: "reps" },
  timedReps: { unit: "reps", byDuration: true },
  timedTotal: { unit: "reps", byDuration: true },
  longestHold: { unit: "sec" },
  distance: { unit: "m" },
  totalDistance: { unit: "m" },
  pace: { unit: "pace", lowerIsBetter: true },
};

/** The selectable metrics of an exercise, the main one first. */
export function metricsFor(e: Measured): MetricKey[] {
  switch (e.measure) {
    case "reps": return weighted(e) ? ["e1rm", "maxWeight", "volume"] : ["maxReps", "totalReps"];
    case "timed": return ["timedReps", "timedTotal"];
    case "hold": return ["longestHold"];
    case "distance": return ["distance", "totalDistance", "pace"];
  }
}

const max = (values: (number | undefined)[]) => {
  const v = values.filter((x): x is number => x !== undefined);
  return v.length ? Math.max(...v) : undefined;
};
const min = (values: (number | undefined)[]) => {
  const v = values.filter((x): x is number => x !== undefined);
  return v.length ? Math.min(...v) : undefined;
};
const sum = (values: (number | undefined)[]) => {
  const v = values.filter((x): x is number => x !== undefined);
  return v.length ? Math.round(v.reduce((a, b) => a + b, 0) * 100) / 100 : undefined;
};

/**
 * The metric of one entry's sets; undefined when no set has the values for it. `durationSec` picks the sets of a
 * `timed` metric (default: the entry's main duration).
 */
export function metricValue(sets: SessionSet[], key: MetricKey, durationSec?: number): number | undefined {
  let w = workingSets(sets);
  if (METRICS[key].byDuration) {
    const d = durationSec ?? mainDuration(sets);
    w = w.filter((s) => s.durationSec === d);
  }
  switch (key) {
    case "e1rm": return max(w.map(e1rm));
    case "maxWeight": return max(w.filter((s) => pos(s.reps)).map((s) => s.weightKg).filter(pos));
    case "volume": return sum(w.map((s) => (pos(s.weightKg) && s.reps !== undefined ? s.weightKg * s.reps : undefined)));
    case "maxReps": case "timedReps": return max(w.map((s) => s.reps));
    case "totalReps": case "timedTotal": return sum(w.map((s) => s.reps));
    case "longestHold": return max(w.map((s) => s.durationSec).filter(pos));
    case "distance": return max(w.map((s) => s.distanceM).filter(pos));
    case "totalDistance": return sum(w.map((s) => s.distanceM).filter(pos));
    case "pace": return min(w.map(pace));
  }
}

/** The duration most working sets with reps had (a tie goes to the longer one), for `timed` exercises. */
export function mainDuration(sets: SessionSet[]): number | undefined {
  const count = new Map<number, number>();
  for (const s of workingSets(sets)) if (s.durationSec !== undefined && s.reps !== undefined) count.set(s.durationSec, (count.get(s.durationSec) ?? 0) + 1);
  let best: number | undefined;
  for (const [d, n] of count) if (best === undefined || n > count.get(best)! || (n === count.get(best) && d > best)) best = d;
  return best;
}

/** The main duration the exercise was last done with as `timed` (the default when switching to that variant). */
export function lastTimedDuration(sessions: Session[], e: Pick<Exercise, "id" | "measure">): number | undefined {
  const entries = exerciseEntries(sessions, sameVariant(e, "timed"));
  for (let i = entries.length - 1; i >= 0; i--) {
    const d = mainDuration(entries[i].sets);
    if (d !== undefined) return d;
  }
  return undefined;
}

/** The durations of an exercise's timed sets over all entries, the most used first (choices for the chart). */
export function timedDurations(entries: Entry[]): number[] {
  const count = new Map<number, number>();
  for (const e of entries) {
    const d = mainDuration(e.sets);
    if (d !== undefined) count.set(d, (count.get(d) ?? 0) + 1);
  }
  return [...count].sort((a, b) => b[1] - a[1] || b[0] - a[0]).map(([d]) => d);
}

export interface Point {
  sessionId: string;
  endedAt: number;
  value: number;
}

/** The chart's points: one per entry that has the metric (for `timed` metrics only entries of that main duration). */
export function series(entries: Entry[], key: MetricKey, durationSec?: number): Point[] {
  const out: Point[] = [];
  for (const e of entries) {
    if (METRICS[key].byDuration && mainDuration(e.sets) !== durationSec) continue;
    const value = metricValue(e.sets, key, durationSec);
    if (value !== undefined) out.push({ sessionId: e.sessionId, endedAt: e.endedAt, value });
  }
  return out;
}

const better = (key: MetricKey, a: number, b: number) => (METRICS[key].lowerIsBetter ? a < b : a > b);

export interface Comparison extends Point {
  /** value minus the value last time (undefined the first time) */
  sinceLast?: number;
  /** value minus the best before (undefined the first time) */
  sinceBest?: number;
  /** better than every time before (never on the first time: there was nothing to beat) */
  newBest: boolean;
  /** compared with last time: up = better (for pace a lower number), down = worse */
  trend?: "up" | "down" | "same";
}

/** Each point against the one before and the best before it ("+6 Reps ggü. letztem Mal", "Bestwert"). */
export function compare(points: Point[], key: MetricKey): Comparison[] {
  let best: number | undefined;
  return points.map((p, i) => {
    const last = points[i - 1]?.value;
    const c: Comparison = { ...p, newBest: best !== undefined && better(key, p.value, best) };
    if (last !== undefined) {
      c.sinceLast = round2(p.value - last);
      c.trend = p.value === last ? "same" : better(key, p.value, last) ? "up" : "down";
    }
    if (best !== undefined) c.sinceBest = round2(p.value - best);
    if (best === undefined || better(key, p.value, best)) best = p.value;
    return c;
  });
}

const round2 = (n: number) => Math.round(n * 100) / 100;

/** The main metric's comparison for every entry (timed: against entries of the same duration), oldest first. */
export function entryComparisons(entries: Entry[], e: Measured): Map<string, Comparison & { key: MetricKey; durationSec?: number }> {
  const key = metricsFor(e)[0];
  const out = new Map<string, Comparison & { key: MetricKey; durationSec?: number }>();
  const durations = METRICS[key].byDuration ? timedDurations(entries) : [undefined];
  for (const d of durations) for (const c of compare(series(entries, key, d), key)) out.set(c.sessionId, { ...c, key, durationSec: d });
  return out;
}

// --- best set ---

/**
 * The set shown for an entry in lists ("best set" in the history): weighted reps the highest estimated 1RM,
 * otherwise the most reps, the longest hold, the longest distance (faster on a tie).
 */
export function bestSet(sets: SessionSet[], e: Measured): SessionSet | undefined {
  const w = workingSets(sets);
  const score = (s: SessionSet): number[] => {
    switch (e.measure) {
      case "reps": return weighted(e) ? [e1rm(s) ?? -1, s.reps ?? -1] : [s.reps ?? -1];
      case "timed": return [s.reps ?? -1, -(s.durationSec ?? 0)];
      case "hold": return [s.durationSec ?? -1];
      case "distance": return [s.distanceM ?? -1, -(pace(s) ?? Infinity)];
    }
  };
  let best: SessionSet | undefined;
  let bestScore: number[] = [];
  for (const s of w) {
    const sc = score(s);
    if (sc[0] < 0) continue;
    const i = sc.findIndex((v, j) => v !== bestScore[j]);
    if (!best || (i >= 0 && sc[i] > bestScore[i])) { best = s; bestScore = sc; }
  }
  return best;
}

// --- records ---

/**
 * weighted reps: best estimated 1RM, max weight, max volume in a set and in a session, most reps at each weight;
 * bodyweight reps: most reps in a set and in a session; timed: most reps at each duration; hold: longest; distance:
 * longest.
 */
export type RecordKind =
  | "e1rm" | "maxWeight" | "setVolume" | "sessionVolume" | "repsAtWeight" | "maxReps" | "sessionReps" | "timedReps"
  | "longestHold" | "longestDistance";

export const RECORD_UNITS: Record<RecordKind, Unit> = {
  e1rm: "kg", maxWeight: "kg", setVolume: "kg", sessionVolume: "kg", repsAtWeight: "reps", maxReps: "reps",
  sessionReps: "reps", timedReps: "reps", longestHold: "sec", longestDistance: "m",
};

export interface Rec {
  kind: RecordKind;
  /** the weight (repsAtWeight) or duration (timedReps) the record is for */
  at?: number;
  value: number;
  /** the set that set it (none for the session totals) */
  set?: SessionSet;
}

export interface HeldRecord extends Rec {
  sessionId: string;
  endedAt: number;
}

/** The record values of one entry (the best per kind and `at`). */
export function entryRecords(sets: SessionSet[], e: Measured): Rec[] {
  const w = workingSets(sets);
  const best = new Map<string, Rec>();
  const offer = (kind: RecordKind, value: number | undefined, set?: SessionSet, at?: number) => {
    if (value === undefined || !(value > 0)) return;
    const id = at === undefined ? kind : `${kind}@${at}`;
    const old = best.get(id);
    if (!old || value > old.value) best.set(id, { kind, value, ...(at !== undefined ? { at } : {}), ...(set ? { set } : {}) });
  };
  switch (e.measure) {
    case "reps":
      if (weighted(e)) {
        for (const s of w) {
          if (!pos(s.weightKg) || !pos(s.reps)) continue;
          offer("e1rm", e1rm(s), s);
          offer("maxWeight", s.weightKg, s);
          offer("setVolume", round2(s.weightKg * s.reps), s);
          offer("repsAtWeight", s.reps, s, s.weightKg);
        }
        offer("sessionVolume", metricValue(sets, "volume"));
      } else {
        for (const s of w) offer("maxReps", s.reps, s);
        offer("sessionReps", metricValue(sets, "totalReps"));
      }
      break;
    case "timed":
      for (const s of w) if (s.durationSec !== undefined) offer("timedReps", s.reps, s, s.durationSec);
      break;
    case "hold":
      for (const s of w) offer("longestHold", s.durationSec, s);
      break;
    case "distance":
      for (const s of w) offer("longestDistance", s.distanceM, s);
      break;
  }
  return [...best.values()];
}

const recordId = (r: Rec) => (r.at === undefined ? r.kind : `${r.kind}@${r.at}`);
const KIND_ORDER: RecordKind[] = ["e1rm", "maxWeight", "setVolume", "sessionVolume", "maxReps", "sessionReps", "longestHold", "longestDistance", "timedReps", "repsAtWeight"];
const sortRecords = <T extends Rec>(list: T[]) =>
  list.sort((a, b) => KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind) || (a.at ?? 0) - (b.at ?? 0));

/** The records an exercise holds now; the earliest session that reached a value keeps it. */
export function exerciseRecords(entries: Entry[], e: Measured): HeldRecord[] {
  const held = new Map<string, HeldRecord>();
  for (const en of entries) {
    for (const r of entryRecords(en.sets, e)) {
      const old = held.get(recordId(r));
      if (!old || r.value > old.value) held.set(recordId(r), { ...r, sessionId: en.sessionId, endedAt: en.endedAt });
    }
  }
  return sortRecords([...held.values()]);
}

export interface NewRecord extends Rec {
  exerciseId: string;
  /** the variant the record is for */
  measure: Measure;
  /** the record before this session */
  previous: number;
}

/**
 * The records each session set (the PR badges): per exercise, a value better than every earlier session's. The first
 * time a record exists (first session with the exercise, first time at a weight or duration) is no badge: there was
 * nothing to beat. Keyed by session id; sessions without records are left out.
 */
export function recordTimeline(sessions: Session[], exercises: Exercise[]): Map<string, NewRecord[]> {
  const byId = new Map(exercises.map((e) => [e.id, e]));
  const held = new Map<string, number>();
  const out = new Map<string, NewRecord[]>();
  for (const s of sortSessions(sessions)) {
    const found: NewRecord[] = [];
    // one group per exercise and variant
    const groups = new Map<string, { e: Exercise; measure: Measure; sets: SessionSet[] }>();
    for (const x of s.exercises) {
      const glossary = byId.get(x.exerciseId);
      if (!glossary) continue;
      const measure = entryMeasure(glossary, x);
      const g = groups.get(`${x.exerciseId} ${measure}`);
      if (g) g.sets.push(...x.sets);
      else groups.set(`${x.exerciseId} ${measure}`, { e: asEntry(glossary, x)!, measure, sets: [...x.sets] });
    }
    for (const [group, { e, measure, sets }] of groups) {
      const exerciseId = e.id;
      const mine: NewRecord[] = [];
      for (const r of entryRecords(sets, e)) {
        const id = `${group} ${recordId(r)}`;
        const previous = held.get(id);
        if (previous !== undefined && r.value > previous) mine.push({ ...r, exerciseId, measure, previous });
        if (previous === undefined || r.value > previous) held.set(id, r.value);
      }
      found.push(...sortRecords(mine));
    }
    if (found.length) out.set(s.id, found);
  }
  return out;
}
